import { useState, useEffect, useRef, useCallback, TouchEvent } from 'react'
import './ImageSlider2.css'
import styles from './ImageSlider2.module.css'
import classNames from 'classnames'

export interface ImageSlide {
  id: string | number
  src: string
  alt: string
  title?: string
  description?: string
}

interface TouchImageSliderProps {
  images: ImageSlide[]
  autoPlay?: boolean
  autoPlayInterval?: number
  showDots?: boolean
  showArrows?: boolean
  showThumbnails?: boolean
  infinite?: boolean
  transitionDuration?: number
  onSlideChange?: (index: number) => void
  className?: string
}

export const ImageSlider2 = (props: TouchImageSliderProps) => {
    const {
        images,
        autoPlay = false,
        autoPlayInterval = 5000,
        showDots = true,
        showArrows = true,
        showThumbnails = false,
        infinite = true,
        transitionDuration = 300,
        onSlideChange,
        className
    } = props

    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false)
    const [touchStartX, setTouchStartX] = useState<number>(0)
    const [touchStartY, setTouchStartY] = useState<number>(0)
    const [touchEndX, setTouchEndX] = useState<number>(0)
    const [isDragging, setIsDragging] = useState<boolean>(false)
    const [dragOffset, setDragOffset] = useState<number>(0)
    
  const sliderRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout>();
  const transitionTimeoutRef = useRef<NodeJS.Timeout>();

  // Calculate slide width
  const getSlideWidth = useCallback(() => {
    if (!sliderRef.current) return 0;
    return sliderRef.current.clientWidth;
  }, []);

  // Go to specific slide
  const goToSlide = useCallback((index: number) => {
    if (isTransitioning || index === currentIndex) return;
    
    setIsTransitioning(true);
    setCurrentIndex(index);
    
    if (onSlideChange) {
      onSlideChange(index);
    }
    
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, transitionDuration);
  }, [currentIndex, isTransitioning, onSlideChange, transitionDuration]);

  // Next slide
  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    
    if (currentIndex === images.length - 1) {
      if (infinite) {
        goToSlide(0);
      }
    } else {
      goToSlide(currentIndex + 1);
    }
  }, [currentIndex, images.length, infinite, goToSlide, isTransitioning]);

  // Previous slide
  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    
    if (currentIndex === 0) {
      if (infinite) {
        goToSlide(images.length - 1);
      }
    } else {
      goToSlide(currentIndex - 1);
    }
  }, [currentIndex, images.length, infinite, goToSlide, isTransitioning]);

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || images.length <= 1) return;
    
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    
    autoPlayRef.current = setInterval(() => {
      if (!isDragging) {
        nextSlide();
      }
    }, autoPlayInterval);
    
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [autoPlay, autoPlayInterval, images.length, nextSlide, isDragging]);

  const handleTouchStart = (e: TouchEvent) => {
    if (isTransitioning) return;
    
    const touch = e.touches[0]
    setTouchStartX(touch.clientX)
    setTouchStartY(touch.clientY)
    setTouchEndX(touch.clientX)
    setIsDragging(true)
    setDragOffset(0)
    
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || isTransitioning) return;
    
    const touch = e.touches[0];
    setTouchEndX(touch.clientX);
    
    const deltaX = touchStartX - touch.clientX;
    setDragOffset(deltaX);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    const distance = touchStartX - touchEndX;
    const slideWidth = getSlideWidth();
    const swipeThreshold = slideWidth * 0.2; // 20% of slide width
    
    if (Math.abs(distance) > swipeThreshold) {
      if (distance > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    
    setDragOffset(0);
    
    if (autoPlay && images.length > 1) {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
      autoPlayRef.current = setInterval(() => {
        nextSlide();
      }, autoPlayInterval);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTransitioning) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          prevSlide();
          break;
        case 'ArrowRight':
          nextSlide();
          break;
        case 'Home':
          goToSlide(0);
          break;
        case 'End':
          goToSlide(images.length - 1);
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prevSlide, nextSlide, goToSlide, images.length, isTransitioning]);


  const getTransformStyle = () => {
    const baseTranslate = -currentIndex * 100;
    const dragTranslate = (dragOffset / getSlideWidth()) * 100;

    return {
      transform: `translateX(calc(${baseTranslate}% - ${dragOffset}px))`,
      transition: isDragging ? 'none' : `transform ${transitionDuration}ms ease-in-out`
    }
  }


  const handleDotClick = (index: number) => {
    goToSlide(index)
  }

  const handleThumbnailClick = (index: number) => {
    goToSlide(index)
  }

  const touchImageSlider = classNames(styles.ImageSlider, className)

  return (
    <div className={touchImageSlider}>
      <div 
        className="slider-container"
        ref={sliderRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={(e) => {
          // Add mouse drag support
          // if (isTransitioning) return;
          // setTouchStartX(e.clientX);
          // setTouchEndX(e.clientX);
          // setIsDragging(true);
          // setDragOffset(0);
        }}
        onMouseMove={(e) => {
          if (!isDragging || isTransitioning) return;
          setTouchEndX(e.clientX);
          const deltaX = touchStartX - e.clientX;
          setDragOffset(deltaX);
        }}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
      >
        <div 
          className="slider-track"
          style={getTransformStyle()}
        >
          {images.map((image, index) => (
            <div 
              key={image.id}
              className={`slide ${index === currentIndex ? 'active' : ''}`}
              aria-hidden={index !== currentIndex}
            >
              <img 
                src={image.src} 
                alt={image.alt}
                loading={index === 0 || index === currentIndex + 1 || index === currentIndex + 2 ? 'eager' : 'lazy'}
              />
              {/* {(image.title || image.description) && (
                <div className="slide-caption">
                  {image.title && <h3>{image.title}</h3>}
                  {image.description && <p>{image.description}</p>}
                </div>
              )} */}
            </div>
          ))}
        </div>

        {showArrows && images.length > 1 && (
          <>
            <button 
              className="slider-arrow prev-arrow"
              onClick={prevSlide}
              aria-label="Previous slide"
              disabled={!infinite && currentIndex === 0}
            >
                 <svg width="8" height="13" viewBox="0 0 8 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.5 12L1 6.5L6.5 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            <button 
              className="slider-arrow next-arrow"
              onClick={nextSlide}
              aria-label="Next slide"
              disabled={!infinite && currentIndex === images.length - 1}
            >
                  <svg width="8" height="13" viewBox="0 0 8 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 1L7 6.5L1.5 12" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
            </button>
          </>
        )}

        {/* <div className="slide-counter">
          {currentIndex + 1} / {images.length}
        </div> */}

        {showDots && images.length > 1 && (
          <div className={styles.ImageSlider__DotsContainer}>
            {images.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={index === currentIndex}
              />
            ))}
          </div>
        )}
      </div>



      {/* {showThumbnails && images.length > 1 && (
        <div className="thumbnails-container">
          {images.map((image, index) => (
            <button
              key={image.id}
              className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
              onClick={() => handleThumbnailClick(index)}
              aria-label={`View slide ${index + 1}`}
            >
              <img 
                src={image.src} 
                alt={`Thumbnail for ${image.alt}`}
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )} */}
    </div>
  )
}